/**
 * The one place a failed action tells the technician it failed.
 *
 * Actions are optimistic: the row moves the instant it is tapped and the
 * cache is rolled back if the request fails. Without this, that rollback is
 * silent — the card slides up, turns green, then slides back down and goes
 * grey with no explanation, which reads as a glitch rather than a refusal.
 *
 * Three decisions worth keeping:
 *
 * **It does not auto-dismiss.** A tech looks up from the car three seconds
 * after tapping. A message that has faded by then is barely better than none,
 * and the board will be quietly disagreeing with what they thought they did.
 * It leaves on a tap, on the next successful action, or on a retry.
 *
 * **It sits above the bottom action bar**, not at the top of the page. That
 * bar is fixed to the viewport, the list behind it scrolls, and a banner
 * pinned to the top of a scrolled page can be entirely off-screen at the
 * moment it has something to say.
 *
 * **Retry is offered only where retrying could work.** A dropped request
 * deserves the button; a 409 will be a 409 however many times it is tapped,
 * and offering a retry there teaches the tech that the button lies.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { WarningCircle, X } from '@phosphor-icons/react'
import { cn } from '@vollgas/shared/utils'
import { useI18n } from '@/i18n/TranslationContext'
import { actionErrorMessage, isRetryable } from '@/lib/actionError'

interface ToastState {
  readonly message: string
  readonly onRetry?: () => void
}

interface ActionToastApi {
  /** Report a failed action. `retry` is offered only if the error looks
   *  worth retrying — pass the action to re-run, not a handler. */
  readonly reportError: (error: unknown, retry?: () => void) => void
  /** A successful action clears whatever failure is on screen. */
  readonly clear: () => void
}

const ActionToastContext = createContext<ActionToastApi | null>(null)

export function useActionToast(): ActionToastApi {
  const ctx = useContext(ActionToastContext)
  if (!ctx) throw new Error('useActionToast must be used inside <ActionToastProvider>')
  return ctx
}

export function ActionToastProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const [toast, setToast] = useState<ToastState | null>(null)
  // The provider sits above the router, so `t` changing locale must not
  // rebuild the API and re-render every consumer.
  const tRef = useRef(t)
  tRef.current = t

  const clear = useCallback(() => setToast(null), [])
  const reportError = useCallback((error: unknown, retry?: () => void) => {
    setToast({
      message: actionErrorMessage(error, tRef.current),
      onRetry: retry && isRetryable(error) ? retry : undefined,
    })
  }, [])

  const api = useMemo(() => ({ reportError, clear }), [reportError, clear])

  return (
    <ActionToastContext.Provider value={api}>
      {children}
      {toast && (
        <div
          role="alert"
          // Above the bottom bar's own fixed strip, and clear of the home
          // indicator on a phone.
          className={cn(
            'fixed inset-x-3 bottom-[6.5rem] z-50 flex items-start gap-2.5 rounded-md border border-accent/40',
            'bg-bg-elevated px-3.5 py-3 shadow-lg',
            'motion-safe:animate-bottombar-in motion-reduce:animate-fade-in',
          )}
        >
          <WarningCircle size={18} weight="fill" className="mt-px flex-shrink-0 text-accent" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-13 font-semibold text-white">{toast.message}</p>
          {toast.onRetry && (
            <button
              type="button"
              onClick={() => {
                const retry = toast.onRetry
                setToast(null)
                retry?.()
              }}
              className="flex-shrink-0 rounded-full border border-white/25 px-3 py-1 text-12 font-bold text-white transition hover:bg-white/10"
            >
              {t.errors.retry}
            </button>
          )}
          <button
            type="button"
            onClick={clear}
            aria-label={t.errors.dismiss}
            className="flex-shrink-0 rounded-full p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <X size={14} weight="bold" aria-hidden="true" />
          </button>
        </div>
      )}
    </ActionToastContext.Provider>
  )
}
